import * as React from "react";
import { Loader2 } from "lucide-react";
import { LogoAnimation } from "../Logo/logoAnimation";
import { cn } from "@/lib/utils";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "default"
    | "outline"
    | "ghost"
    | "destructive"
    | "icon"
    | "outlinedIcon";
  size?: "sm" | "md" | "lg" | "xs" | "xxs";
  color?: "error";
  loading?: boolean;
  loadingPosition?: "start" | "end";
  icon?: React.ReactNode;
  iconPosition?: "start" | "end";
  selected?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "md",
      color,
      loading = false,
      loadingPosition = "start",
      icon,
      iconPosition = "start",
      selected = false,
      disabled,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const [ripples, setRipples] = React.useState<
      Array<{ x: number; y: number; size: number; id: number }>
    >([]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) return;

      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;

      const newRipple = {
        x,
        y,
        size,
        id: Date.now(),
      };

      setRipples((prev) => [...prev, newRipple]);

      // Remove ripple after animation
      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
      }, 600);

      onClick?.(e);
    };

    // Base styles
    const baseStyles =
      "group/btn uppercase inline-flex items-center justify-center gap-2 font-medium rounded-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 relative overflow-hidden";

    // Variant styles
    const variantStyles = {
      default:
        "bg-primary text-white hover:bg-primary/90 focus-visible:ring-primary disabled:opacity-40 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600",
      outline:
        "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 focus-visible:ring-gray-400 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-500",
      ghost:
        "text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400 disabled:opacity-40 disabled:cursor-not-allowed dark:text-gray-200 dark:hover:bg-gray-800",
      destructive:
        "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-red-700 dark:hover:bg-red-800",
      icon: "text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400 disabled:opacity-40 disabled:cursor-not-allowed dark:text-gray-200 dark:hover:bg-gray-800",
      outlinedIcon:
        "text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 focus-visible:ring-gray-400 disabled:opacity-40 disabled:cursor-not-allowed dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-500",
    };

    // Selected state styles (when selected=true)
    const selectedStyles = {
      default: "bg-primary text-white ring-2 ring-primary/50",
      outline:
        "bg-primary/10 text-primary border-primary ring-2 ring-primary/20 dark:bg-primary/20",
      ghost: "bg-primary/10 text-primary dark:bg-primary/20",
      destructive: "bg-red-700 text-white ring-2 ring-red-600/50",
      icon: "bg-primary/10 text-primary dark:bg-primary/20",
      outlinedIcon:
        "bg-primary/10 text-primary border-primary ring-2 ring-primary/20 dark:bg-primary/20",
    };

    // Color overrides
    const colorStyles = {
      error:
        "text-red-600 border-red-200 hover:bg-red-100 hover:border-red-300 focus-visible:ring-red-600 disabled:opacity-40 disabled:cursor-not-allowed dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950/50 dark:hover:border-red-700",
    };

    // Size styles
    const sizeStyles = {
      xxs: "text-[10px] px-1.5 py-1 h-6",
      xs: "text-xs px-2 py-1.5 h-7",
      sm: "text-xs px-3 py-1.5 h-8",
      md: "text-sm px-4 py-2 h-10",
      lg: "text-base px-6 py-3 h-12",
    };

    // Icon button size overrides (square buttons)
    const iconSizeStyles = {
      xxs: "text-[10px] p-0.5 h-5 w-5",
      xs: "text-xs p-1 h-7 w-7",
      sm: "text-xs p-1.5 h-8 w-8",
      md: "text-sm p-2 h-10 w-10",
      lg: "text-base p-3 h-12 w-12",
    };

    const spinner = (
      <LogoAnimation
        size={
          size === "xxs" ? 12 : size === "sm" ? 14 : size === "lg" ? 18 : 16
        }
        variant="pulse"
        className={cn({
          "bg-muted": variant === "default",
          "bg-primary": variant !== "default",
        })}
      />
    );

    const startContent =
      loading && loadingPosition === "start"
        ? spinner
        : icon && iconPosition === "start"
        ? icon
        : null;
    const endContent =
      loading && loadingPosition === "end"
        ? spinner
        : icon && iconPosition === "end"
        ? icon
        : null;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          baseStyles,
          selected
            ? selectedStyles[variant]
            : color
            ? colorStyles[color]
            : variantStyles[variant],
          variant === "icon" || variant === "outlinedIcon"
            ? iconSizeStyles[size]
            : sizeStyles[size],
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {/* Ripple effect */}
        {ripples.map((ripple) => (
          <span
            key={ripple.id}
            style={{
              position: "absolute",
              left: ripple.x,
              top: ripple.y,
              width: ripple.size,
              height: ripple.size,
              borderRadius: "50%",
              transform: "scale(0)",
              animation: "ripple 600ms ease-out",
              backgroundColor:
                variant === "default" || variant === "destructive"
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(0, 0, 0, 0.2)",
              pointerEvents: "none",
            }}
          />
        ))}
        <span
          className={cn(
            "relative z-10 inline-flex items-center justify-center gap-2",
            {
              "group-hover/btn:scale-110 transition-transform":
                variant === "icon" || variant === "outlinedIcon",
            }
          )}
        >
          {startContent}
          {children}
          {endContent}
        </span>
        <style>{`
          @keyframes ripple {
            to {
              transform: scale(2);
              opacity: 0;
            }
          }
        `}</style>
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };
